<template>
  <v-card :loading="loading">
    <v-system-bar window class="mb-2">
      {{ title }}<v-spacer />
      <v-btn @click="$emit('close')" icon>
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-system-bar>
    <v-card-text>
      <v-form ref="form" v-model="valid" :disabled="loading">
        <v-text-field
          v-if="!loading"
          v-model="item.title"
          label="Titel"
          required
          :rules="titleRules"
          autofocus
        ></v-text-field>

        <LookupValueBtnToggle
          resource="register/notetype"
          v-model="item.type"
          label="Art"
          mandatory
          :color="color"
        />

        <v-switch
          class="ml-1"
          v-if="item.type.code == 'exam'"
          v-model="item.plannedExam"
          :label="` ${item.plannedExam ? 'ja' : 'nein'}`"
          inset
        >
          <template v-slot:label>
            <span
              >im Probenplan eintragen:
              {{ item.plannedExam ? "ja" : "nein" }}</span
            >
            <v-spacer></v-spacer>
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on"
                  >mdi-information-outline</v-icon
                >
              </template>
              <span
                >Vorträge und Proben welche nur einzelne Schüler:innen einer
                Klasse (z.B. EF) betreffen, sollten nicht im Probenplan
                erscheinen.</span
              >
            </v-tooltip>
          </template>
        </v-switch>
        <v-switch
          class="ml-1"
          v-model="item.smartLearn"
          :label="`smartlearn: ${item.smartLearn ? 'ja' : 'nein'}`"
          inset
        />

        <DateInput
          v-if="selectLesson"
          label="Datum"
          v-model="item.date"
          :allowed-dates="allowedDates"
          required
        ></DateInput>

        <LookupValueInput
          v-if="selectLesson"
          label="Standort"
          v-model="item.site"
          resource="common/site"
          required
        />
        <PeriodInput
          v-if="selectLesson"
          v-model="item.period"
          :site="item.site"
          required
        ></PeriodInput>

        <v-textarea
          v-model="item.description"
          label="Text"
          outlined
        ></v-textarea>

        <v-text-field
          v-model="item.url"
          label="URL (Link)"
          placeholder="https://"
        ></v-text-field>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>
    <v-card-actions>
      <v-btn text @click="close">abbrechen </v-btn>
      <v-spacer class="mx-8"></v-spacer>
      <v-btn
        text
        color="danger"
        @click="trash"
        :loading="trashing"
        v-if="!isNew"
        >löschen</v-btn
      >
      <v-btn
        text
        color="primary"
        @click="save"
        :loading="saving"
        :disabled="!valid"
        >speichern</v-btn
      >
    </v-card-actions>
  </v-card>
</template>
<script>
import { defineComponent } from "vue";

import DateInput from "common/components/DateInput.vue";
import PeriodInput from "common/components/PeriodInput.vue";
import LookupValueInput from "common/components/LookupValueInput.vue";
import LookupValueBtnToggle from "common/components/LookupValueBtnToggle.vue";

import { today } from "common/utils/date.js";
import { noteColor } from "common/utils/icons.js";

export default defineComponent({
  name: "NoteForm",
  components: {
    DateInput,
    PeriodInput,
    LookupValueInput,
    LookupValueBtnToggle,
  },
  props: ["id", "defaults", "site"],
  data() {
    return {
      item: {
        id: 0,
        title: "",
        description: "",
        url: "",
        type: { code: "", description: "" },
        site: null,
      },
      loading: false,
      valid: false,
      saving: false,
      trashing: false,
      titleRules: [(v) => !!v || "Titel ist obligatorisch"],
      types: [],
    };
  },
  computed: {
    isNew() {
      return this.id == 0;
    },
    selectLesson() {
      return !this.isNew || !this.item.period;
    },
    title() {
      return this.isNew ? "Neue Notiz" : "Notiz bearbeiten";
    },
    color() {
      return noteColor(this.item);
    },
  },
  watch: {
    defaults() {
      this.fetchData();
    },
    id() {
      this.fetchData();
    },
    "item.type"() {
      if (this.isNew) {
        if (this.item.type.code == "exam") {
          this.item.plannedExam = true;
        } else {
          this.item.plannedExam = false;
        }
      }
    },
  },
  methods: {
    allowedDates: (val) => val >= today(),
    close() {
      this.fetchData();
      this.$emit("close");
    },
    async fetchData() {
      this.loading = true;
      if (this.id) {
        this.item = await this.apiGet({
          resource: "register/note",
          id: this.id,
        });
      } else {
        this.item = {
          id: 0,
          title: "",
          description: "",
          url: "",
          type: { code: "", description: "" },
          site: this.site,
          ...this.defaults,
        };

        if (this.site) {
          const periods = await this.apiList({
            resource: "common/period",
            query: `site=${this.site ? this.site.id : ""}`,
          });
          this.item.period = periods.find(
            (el) => el.startTime == this.defaults.startTime
          );
        } else {
          this.item.period = null;
        }
      }
      this.loading = false;
    },
    async save() {
      this.saving = true;
      await this.apiSave({
        add: this.isNew,
        resource: "register/note",
        data: this.item,
      });
      this.saving = false;
      this.$emit("updated");
      this.$emit("close");
    },
    async trash() {
      if (
        await this.$root.confirm({
          message: "soll diese Notiz gelöscht werden?",
          icon: "mdi-trash-can",
          color: "danger",
        })
      ) {
        this.trashing = true;
        await this.apiDelete({ resource: "register/note", id: this.item.id });
        this.trashing = false;
        this.$emit("updated");
        this.$emit("close");
      }
    },
  },
  mounted() {
    this.fetchData();
  },
});
</script>
